import * as React from 'react';
import * as ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import {
  i18nReducer,
  loadTranslations,
  setLocale,
  syncTranslationWithStore,
} from 'react-redux-i18n';
import { combineReducers } from 'redux';

import { Editor } from './components/editor';
import { Home } from './components/home';
import { Result } from './components/result';
import { mountReactNode } from '../../common/utils/react';
import { createStore } from '@common/utils/redux';
import { translationsEN } from './translations/en';
import { translationsES } from './translations/es';
import { translationsJA } from './translations/ja';
import { datasheetFormatPreferenceReducer } from './reducers/formatPreference';
import {
  clearDatasheetTabPreferencesByPrefix as clearTabPreferencesByPrefixAction,
  datasheetTabPreferenceReducer,
} from './reducers/tabPreference';

// Shared store instance for datasheet results to persist format preferences across individual selections
let sharedDatasheetStore: any = null;

const getOrCreateSharedStore = () => {
  if (!sharedDatasheetStore) {
    sharedDatasheetStore = createStore(
      combineReducers({
        i18n: i18nReducer,
        datasheetFormatPreference: datasheetFormatPreferenceReducer,
        datasheetTabPreference: datasheetTabPreferenceReducer,
      })
    );
    // Expose shared store to window for access from controllers
    if (typeof window !== 'undefined') {
      (window as any).__REDUX_STORE__ = (window as any).__REDUX_STORE__ || {};
      (window as any).__REDUX_STORE__.datasheet = sharedDatasheetStore;
      (window as any).__REDUX_STORE__.latest = sharedDatasheetStore;
    }
  }
  return sharedDatasheetStore;
};

const initComponent = function (componentId: string, locale: string, component: React.ReactNode, useSharedStore: boolean = false) {
  try {
    const store = useSharedStore 
      ? getOrCreateSharedStore()
      : createStore(
          combineReducers({
            i18n: i18nReducer,
            datasheetFormatPreference: datasheetFormatPreferenceReducer,
            datasheetTabPreference: datasheetTabPreferenceReducer,
          })
        );
    
    syncTranslationWithStore(store);
    
    const translation = {
      en: translationsEN,
      ja: translationsJA,
      es: translationsES,
    };
    
    if (!translation[locale as keyof typeof translation]) {
      locale = 'en';
    }
    
    store.dispatch(loadTranslations(translation));
    store.dispatch(setLocale(locale));
    
    mountReactNode(
      <Provider store={store}>
        {component}
      </Provider>,
      document.getElementById(componentId)!
    );
  } catch (err) {
    console.error(err);
  }
};

export const initDatasheetsEditor = (componentId: string, locale: string, context: number, formatId: number, tableFieldId: number, tableContext?: number) => {
  initComponent(componentId, locale, <Editor context={context} formatId={formatId} tableFieldId={tableFieldId} tableContext={tableContext} />);
};

export const initDatasheetsHome = (componentId: string, locale: string) => {
  initComponent(componentId, locale, <Home />);
};

let datasheetResultMountKey = 0;
const datasheetResultRootsByContainerId = new Map<string, ReactDOM.Root>();
const datasheetResultElementsByContainerId = new Map<string, HTMLElement>();

export const initDatasheetsResult = (
  componentId: string,
  locale: string,
  context: number,
  formatId: number,
  tableFieldId: number,
  id: number,
  tableContext?: number,
  formatOnly?: boolean,
  groupKey?: string,
  canSetFolderDefault?: boolean,
  templateOptionOn?: boolean,
  rootFolderId?: number,
  initialRrn?: number
) => {
  datasheetResultMountKey += 1;
  try {
    const store = getOrCreateSharedStore();
    syncTranslationWithStore(store);

    const translation = {
      en: translationsEN,
      ja: translationsJA,
      es: translationsES,
    };

    if (!translation[locale as keyof typeof translation]) {
      locale = 'en';
    }

    store.dispatch(loadTranslations(translation));
    store.dispatch(setLocale(locale));

    const container = document.getElementById(componentId) as HTMLElement | null;
    if (!container) {
      return;
    }

    const previousContainerElement = datasheetResultElementsByContainerId.get(componentId);
    const containerReplaced =
      previousContainerElement != null &&
      (previousContainerElement !== container || !previousContainerElement.isConnected);

    if (containerReplaced) {
      const staleRoot = datasheetResultRootsByContainerId.get(componentId);
      if (staleRoot) {
        try {
          staleRoot.unmount();
        } catch {
        }
      }
      datasheetResultRootsByContainerId.delete(componentId);
      datasheetResultElementsByContainerId.delete(componentId);
    }

    let root = datasheetResultRootsByContainerId.get(componentId);
    if (!root) {
      root = ReactDOM.createRoot(container);
      datasheetResultRootsByContainerId.set(componentId, root);
      datasheetResultElementsByContainerId.set(componentId, container);
    }

    root.render(
      <Provider store={store}>
        <Result
          reloadRecentlyUsedKey={datasheetResultMountKey}
          context={context}
          formatId={formatId}
          tableFieldId={tableFieldId}
          tableContext={tableContext}
          globalId={id}
          formatOnly={formatOnly}
          groupKey={groupKey}
          canSetFolderDefault={canSetFolderDefault}
          templateOptionOn={templateOptionOn}
          rootFolderId={rootFolderId}
          initialRrn={initialRrn}
        />
      </Provider>
    );
  } catch (err) {
    console.error(err);
  }
};

/**
 * Clears persisted datasheet tab selections for pedigree split view (keys prefixed with
 * `tab:split:pedigree:<id>:`). Invoked from GWT when the pedigree tab is closed.
 */
export const clearDatasheetTabPreferencesByPrefix = (prefix: string) => {
  getOrCreateSharedStore().dispatch(clearTabPreferencesByPrefixAction(prefix));
};

/**
 * Unmounts the React datasheet result tree. Call when the GWT host removes the datasheet tab
 * so effect cleanups run (e.g. lock release). Opening another datasheet calls this implicitly
 * when the container id changes; closing the tab does not, so GWT must invoke this from onDetach.
 *
 * @param componentId DOM id of the mount container (from DatasheetResultWnd). When provided, unmount
 *   runs only if this instance still owns the singleton root. Closing a non-selected datasheet tab
 *   otherwise would unmount another tab's React tree and skip the correct lock release.
 */
export const unmountDatasheetsResult = (componentId?: string) => {
  if (componentId == null) {
    for (const [id, root] of datasheetResultRootsByContainerId.entries()) {
      try {
        root.unmount();
      } catch (err) {
        console.error(`unmountDatasheetsResult failed for ${id}`, err);
      }
    }
    datasheetResultRootsByContainerId.clear();
    datasheetResultElementsByContainerId.clear();
    return;
  }

  const root = datasheetResultRootsByContainerId.get(componentId);
  if (!root) {
    return;
  }
  try {
    root.unmount();
  } catch (err) {
    console.error("unmountDatasheetsResult failed", err);
  }
  datasheetResultRootsByContainerId.delete(componentId);
  datasheetResultElementsByContainerId.delete(componentId);
};
